import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import PageBanner from '@/components/feature/PageBanner';
import { aboutValues, aboutVoices, editorPrinciples } from '@/mocks/home';

const contactChannels = [
  {
    icon: 'ri-mail-send-line',
    label: '投稿与线索征集',
    value: 'tougao@17chigua.example',
    note: '接收匿名投稿、私信爆料与各类线索材料',
  },
  {
    icon: 'ri-customer-service-2-line',
    label: '投稿咨询',
    value: 'help@17chigua.example',
    note: '不确定能不能投、想先问问怎么投，都可以发这里',
  },
  {
    icon: 'ri-shield-check-line',
    label: '来源保护与删除请求',
    value: 'privacy@17chigua.example',
    note: '要求删除自己提交的线索或处理隐私相关问题',
  },
];

const milestones = [
  { year: '起步', text: '最初只是一个整理零散线索的编辑习惯，后来发现很多线索在同一种情况下被浪费掉：投稿人不知道往哪投。' },
  { year: '成形', text: '我们把投稿入口做成了固定通道，明确了线索类型、材料标准与处理时间线，让投稿这件事变得可预期。' },
  { year: '现在', text: '投稿入口全天开放，匿名投稿与私信爆料并行，编辑按照统一原则处理每一条线索，无论投稿人是谁。' },
];

const pageSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于我们',
  url: 'https://gzruanke.cn/about',
  inLanguage: 'zh-CN',
  description: '了解17吃瓜网的品牌定位、发展理念、内容原则、用户口碑与投稿联系方式。',
  isPartOf: { '@type': 'WebSite', name: '17吃瓜网', url: 'https://gzruanke.cn/' },
  about: {
    '@type': 'Organization',
    name: '17吃瓜网',
    description: '专注娱乐与网络热点线索征集、核实与内容整理的投稿平台。',
    email: 'tougao@17chigua.example',
  },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://gzruanke.cn/' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://gzruanke.cn/about' },
    ],
  },
};

export default function AboutPage() {
  return (
    <SiteLayout>
      <title>关于我们 - 17吃瓜网的投稿定位与内容原则</title>
      <meta
        name="description"
        content="17吃瓜网是一个专注娱乐与网络热点线索征集的投稿平台。了解我们的网站定位、发展理念、内容原则、用户口碑，以及投稿与来源保护的联系方式。"
      />
      <meta name="keywords" content="17吃瓜网,关于我们,投稿平台,内容原则,爆料联系方式" />
      <meta name="robots" content="index,follow" />
      <link rel="canonical" href="https://gzruanke.cn/about" />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="17吃瓜网" />
      <meta property="og:title" content="关于我们 - 17吃瓜网的投稿定位与内容原则" />
      <meta property="og:description" content="了解17吃瓜网的定位、理念、内容原则与投稿联系方式。" />
      <meta property="og:url" content="https://gzruanke.cn/about" />
      <meta property="og:locale" content="zh_CN" />

      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content="关于我们 - 17吃瓜网的投稿定位与内容原则" />
      <meta name="twitter:description" content="17吃瓜网的定位、理念、内容原则与投稿联系方式。" />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }} />

      <PageBanner
        eyebrow="品牌介绍"
        title="关于我们"
        description="17吃瓜网不是一个追着热点跑的资讯站，而是一个把线索接住、核实清楚、如实呈现的投稿平台。"
        breadcrumb={[{ label: '首页', to: '/' }, { label: '关于我们' }]}
      />

      <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-50">
        <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="w-full h-[300px] md:h-[420px] rounded-lg overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=stylized%20editorial%20still%20life%20of%20stacked%20paper%20clippings%2C%20a%20vintage%20magnifying%20glass%20and%20a%20red%20pencil%20on%20a%20warm%20terracotta%20desk%2C%20bold%20graphic%20art%2C%20dramatic%20warm%20lighting%2C%20paper%20texture%2C%20no%20legible%20text%2C%20no%20people&width=900&height=1000&seq=gossip-about-01&orientation=portrait"
              alt="17吃瓜网编辑部整理线索的工作台抽象插图"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div>
            <span className="font-heading text-sm tracking-widest text-primary-600">网站定位</span>
            <h2 className="mt-3 font-heading text-3xl md:text-4xl text-foreground-950 leading-tight">
              先做通道，再谈内容
            </h2>
            <p className="mt-5 text-sm md:text-base leading-8 text-foreground-600">
              每天都有大量线索在私聊里被提起，又在下一条消息里被淹没。我们想解决的就是这件事：
              给所有掌握信息的人一个固定的<strong className="font-bold text-foreground-900">投稿入口</strong>，
              让线索不再靠运气被看见。
            </p>
            <p className="mt-4 text-sm md:text-base leading-8 text-foreground-600">
              这件事听起来简单，做起来却需要明确规则：什么线索收、材料要到什么程度、
              核实要经过几步、投稿人的身份怎么保护。这些规则构成了17吃瓜网全部的产品形态。
            </p>
            <dl className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-6">
              <div>
                <dt className="text-xs text-foreground-500">开放线索方向</dt>
                <dd className="mt-1 font-heading text-2xl text-foreground-950">8 类</dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">初步核实周期</dt>
                <dd className="mt-1 font-heading text-2xl text-foreground-950">36 小时</dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">投稿人身份</dt>
                <dd className="mt-1 font-heading text-2xl text-foreground-950">仅编辑可见</dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-100">
        <div className="w-full max-w-2xl mb-12">
          <span className="font-heading text-sm tracking-widest text-secondary-700">理念与原则</span>
          <h2 className="mt-3 font-heading text-3xl md:text-5xl text-foreground-950 leading-tight">
            我们靠这三件事判断一条线索
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {aboutValues.map((v) => (
            <article key={v.title} className="p-6 rounded-lg bg-background-50 border border-background-200 flex flex-col">
              <span className="w-12 h-12 flex items-center justify-center rounded-md bg-primary-100 text-primary-700">
                <i className={`${v.icon} text-2xl`}></i>
              </span>
              <h3 className="mt-5 text-lg font-bold text-foreground-950">{v.title}</h3>
              <p className="mt-3 text-sm leading-7 text-foreground-600 flex-1">{v.desc}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="principles" className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-50 scroll-mt-24">
        <div className="w-full grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-4">
            <span className="font-heading text-sm tracking-widest text-primary-600">内容原则</span>
            <h2 className="mt-3 font-heading text-3xl md:text-5xl text-foreground-950 leading-tight">
              四条不妥协的标准
            </h2>
            <p className="mt-5 text-sm md:text-base leading-7 text-foreground-600">
              这四条标准同时约束编辑与投稿人。你可以用它来判断我们是否值得信任，也可以用它来判断自己的线索是否适合投递。
            </p>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {editorPrinciples.map((p) => (
              <article key={p.title} className="flex gap-4 p-6 rounded-lg bg-background-100 border border-background-200">
                <span className="w-11 h-11 shrink-0 flex items-center justify-center rounded-md bg-accent-100 text-accent-800">
                  <i className={`${p.icon} text-xl`}></i>
                </span>
                <div>
                  <h3 className="text-base font-bold text-foreground-950">{p.title}</h3>
                  <p className="mt-2 text-sm leading-7 text-foreground-600">{p.desc}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-primary-950">
        <div className="w-full">
          <div className="max-w-2xl mb-12">
            <span className="font-heading text-sm tracking-widest text-accent-400">发展历程</span>
            <h2 className="mt-3 font-heading text-3xl md:text-5xl text-background-50 leading-tight">
              从编辑习惯到固定通道
            </h2>
          </div>
          <ol className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {milestones.map((m) => (
              <li key={m.year} className="p-6 rounded-lg bg-background-50/8 border border-background-50/15">
                <span className="font-heading text-2xl text-accent-400">{m.year}</span>
                <p className="mt-4 text-sm leading-7 text-background-200">{m.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-100">
        <div className="w-full">
          <div className="max-w-2xl mb-12">
            <span className="font-heading text-sm tracking-widest text-secondary-700">用户口碑</span>
            <h2 className="mt-3 font-heading text-3xl md:text-5xl text-foreground-950 leading-tight">
              他们怎么评价这个投稿入口
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {aboutVoices.map((v) => (
              <figure key={v.name} className="p-6 rounded-lg bg-background-50 border border-background-200 flex flex-col">
                <i className="ri-double-quotes-l text-3xl text-accent-400"></i>
                <blockquote className="mt-4 text-sm leading-7 text-foreground-700 flex-1">{v.quote}</blockquote>
                <figcaption className="mt-6 pt-5 border-t border-background-200">
                  <span className="block text-sm font-bold text-foreground-950">{v.name}</span>
                  <span className="block text-xs text-foreground-500 mt-0.5">{v.role}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-50 scroll-mt-24">
        <div className="w-full grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-5">
            <span className="font-heading text-sm tracking-widest text-primary-600">联系我们</span>
            <h2 className="mt-3 font-heading text-3xl md:text-5xl text-foreground-950 leading-tight">
              分渠道联系，更快得到回应
            </h2>
            <p className="mt-5 text-sm md:text-base leading-8 text-foreground-600">
              为了让线索、咨询和来源保护请求都能被正确处理，我们分成了三个邮箱。
              投稿请优先使用<Link to="/submit" className="font-bold text-primary-600 hover:text-primary-700 cursor-pointer">投稿入口</Link>，其余情况按下面的渠道选择。
            </p>
            <Link
              to="/submit"
              className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-md bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-send-plane-fill text-base"></i>
              前往投稿入口
            </Link>
          </div>
          <div className="lg:col-span-7 flex flex-col gap-4">
            {contactChannels.map((c) => (
              <div key={c.label} className="flex items-start gap-4 p-5 rounded-lg bg-background-100 border border-background-200">
                <span className="w-11 h-11 shrink-0 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className={`${c.icon} text-xl`}></i>
                </span>
                <div className="flex-1">
                  <h3 className="text-sm font-bold text-foreground-950">{c.label}</h3>
                  <p className="mt-1 text-base font-bold text-primary-700 break-all">{c.value}</p>
                  <p className="mt-1.5 text-sm leading-6 text-foreground-600">{c.note}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}